/**
 * Resolve actor / session for InventoryMovements (ADR-0016 Phase 1).
 * Soft fields — never block writes if session missing.
 *
 * Sources (first hit wins for session):
 * 1. Explicit overrides (sale / receive / void meta)
 * 2. currentRole (worker session on login)
 * 3. currentUser
 * 4. Active cash-register session for business (sessionId + workerId)
 */

import { readActorRole } from '../../security/actorGuard';
import { getCurrentBusinessId, normalizeBusinessStorageId } from '../../../utils/sharedCore';
import { kvGet } from '../../../infrastructure/storage/kvAccess';

function readCurrentUserSafe() {
  try {
    const raw = kvGet('currentUser');
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
  } catch {
    /* storage not ready */
  }
  return null;
}

function parseKvObject(raw) {
  if (!raw) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === 'object' ? p : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Soft read of cash-register active session (worker POS shift) via KV.
 * Does not import cashRegisterService (circular-risk free).
 * @param {string|null} businessId
 * @returns {{ sessionId?: string, workerId?: string, loginTime?: string }|null}
 */
function readActiveCashRegisterSession(businessId) {
  if (!businessId) return null;
  try {
    const bid = normalizeBusinessStorageId(businessId) || String(businessId);
    const keys = [
      `cashRegisterData_${bid}`,
      `cashRegisterData_business_${bid}`,
      'cashRegisterData'
    ];
    for (const key of keys) {
      const data = parseKvObject(kvGet(key));
      const session = data?.activeSession;
      if (session && typeof session === 'object') return session;
    }
  } catch {
    /* storage not ready */
  }
  return null;
}

function pickFirstString(...candidates) {
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return String(c);
  }
  return null;
}

/**
 * @param {string|null} [businessId]
 * @param {object} [overrides]
 * @returns {{ actorId: string|null, sessionId: string|null, storeId?: string|null, deviceId?: string|null }}
 */
export function resolveMovementActorMeta(businessId = null, overrides = {}) {
  const bid =
    businessId ||
    overrides.businessId ||
    getCurrentBusinessId({ allowFallback: false }) ||
    null;
  const role = readActorRole(bid);
  const user = readCurrentUserSafe();
  const cash =
    overrides.cashSession !== undefined
      ? overrides.cashSession
      : readActiveCashRegisterSession(bid);

  const actorId =
    pickFirstString(
      overrides.actorId,
      overrides.workerId,
      overrides.userId,
      role?.id,
      role?.workerId,
      cash?.workerId,
      user?.id,
      user?.workerId,
      user?.username,
      user?.email,
      role?.name
    ) || 'system:unattributed';

  const sessionId = pickFirstString(
    overrides.sessionId,
    overrides.cashRegisterSessionId,
    overrides.activeSessionId,
    role?.sessionId,
    role?.activeSessionId,
    role?.cashRegisterSessionId,
    role?.loginTime,
    cash?.sessionId,
    cash?.loginTime,
    user?.sessionId,
    user?.activeSessionId,
    user?.cashRegisterSessionId
  );

  return {
    actorId,
    sessionId,
    storeId:
      overrides.storeId != null && String(overrides.storeId).trim() !== ''
        ? String(overrides.storeId)
        : null,
    deviceId: pickFirstString(overrides.deviceId, user?.deviceId, role?.deviceId)
  };
}
